import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import Fieldset from '../../Common/Components/Fieldset'
import FormRow from '../../Common/Components/FormRow'
import P from '../../Common/Components/P'
import Checkbox from '../../Common/Components/Checkbox'
import TextInput from '../../Common/Components/TextInput'

import { LanguageContext } from '../../Common/Contexts'

import { REPORT_ALARM_CAPABILITY_CALLSTATE_TALKTIME_HIDE } from './Constants'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { CallStateThresholds as CallStateThresholdsLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
// CSS imports. Commented out because of current build process.
// import './callStateThresholds.scss'

/**
 * Component properties type.
 */
type Props = {
    alarmTypesFlags: number
    isOnInbAlertingEnabled: boolean
    setIsOnInbAlertingEnabled: (enabled: boolean) => void
    onInbAlerting: string
    setOnInbAlerting: (newValue: string) => void
    isOnTalkTimeEnabled: boolean
    setIsOnTalkTimeEnabled: (enabled: boolean) => void
    onTalkTime: string
    setOnTalkTime: (newValue: string) => void
}

/**
 * Call state thresholds section in Alarms tab of report configuration modal window.
 *
 * @param alarmTypesFlags The alarming features support flags.
 * @param isOnInbAlertingEnabled Whether the corresponding setting is enabled.
 * @param setIsOnInbAlertingEnabled Setter for whether the corresponding setting is enabled.
 * @param onInbAlerting Report property.
 * @param setOnInbAlerting Setter for corresponding report property.
 * @param isOnTalkTimeEnabled Whether the corresponding setting is enabled.
 * @param setIsOnTalkTimeEnabled Setter for whether the corresponding setting is enabled.
 * @param onTalkTime Report property.
 * @param setOnTalkTime Setter for corresponding report property.
 */
const CallStateThresholds: React.FC<Props> = ({
    alarmTypesFlags,
    isOnInbAlertingEnabled,
    setIsOnInbAlertingEnabled,
    onInbAlerting,
    setOnInbAlerting,
    isOnTalkTimeEnabled,
    setIsOnTalkTimeEnabled,
    onTalkTime,
    setOnTalkTime
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('CallStateThresholds')

    const isCallTimeSettingDisplayed =
        (alarmTypesFlags & REPORT_ALARM_CAPABILITY_CALLSTATE_TALKTIME_HIDE) !==
        REPORT_ALARM_CAPABILITY_CALLSTATE_TALKTIME_HIDE

    return (
        <Fieldset className='display-settings call-state-thresholds'>
            <Heading level={HeadingLevel.ONE}>
                {language.get(CallStateThresholdsLanguage.CallStateThresholds_SectionHeader)}
            </Heading>
            <P>{language.get(CallStateThresholdsLanguage.OninboundalertingPara)}</P>
            <FormRow isInline className={'on-inbound-alerting'}>
                <Checkbox
                    labelLeft={language.get(CallStateThresholdsLanguage.OninboundalertingEnabledHiddenlabel)}
                    isLabelHidden
                    onChange={setIsOnInbAlertingEnabled}
                    isChecked={isOnInbAlertingEnabled}
                />
                <TextInput
                    value={onInbAlerting}
                    onChange={setOnInbAlerting}
                    labelLeft={language.get(CallStateThresholdsLanguage.OninboundalertingLabel)}
                    placeholder={language.get(CallStateThresholdsLanguage.OninboundalertingPlaceholder)}
                    disabled={!isOnInbAlertingEnabled}
                />
            </FormRow>
            {isCallTimeSettingDisplayed && (
                <React.Fragment>
                    <P>{language.get(CallStateThresholdsLanguage.OntalktimePara)}</P>
                    <FormRow isInline className={'on-talk-time'}>
                        <Checkbox
                            labelLeft={language.get(CallStateThresholdsLanguage.OntalktimeEnabledHiddenlabel)}
                            isLabelHidden
                            onChange={setIsOnTalkTimeEnabled}
                            isChecked={isOnTalkTimeEnabled}
                        />
                        <TextInput
                            value={onTalkTime}
                            onChange={setOnTalkTime}
                            labelLeft={language.get(CallStateThresholdsLanguage.OntalktimeLabel)}
                            placeholder={language.get(CallStateThresholdsLanguage.OntalktimePlaceholder)}
                            disabled={!isOnTalkTimeEnabled}
                        />
                    </FormRow>
                </React.Fragment>
            )}
        </Fieldset>
    )
}

export default React.memo(CallStateThresholds)
